/** @jsx jsx */
import { css, jsx } from "@emotion/core";
import { useState } from "react";
import styled from "@emotion/styled";

const Container = styled.button`
  background: none;
  color: white;
  border: 2px solid ${props => props.theme.colors.secondary};
  outline: 0;
  text-transform: uppercase;
  font-size: 1rem;
  font-weight: bold;
  font-family: ${props => props.theme.fonts.subheading};
  padding: 0.375rem 1rem;
  line-height: 1.5rem;
  position: relative;
  z-index: 1;

  &:hover {
    cursor: pointer;
  }

  &:before {
    content: "";
    background: ${props => props.theme.colors.secondary};
    position: absolute;
    top: 0;
    left: 0;
    width: 0;
    height: 100%;
    z-index: -1;
    transition: 0.2s;
  }

  &:hover:before {
    width: 100%;
  }

  &:disabled,
  &[disabled] {
    background: ${props => props.theme.colors.secondary};
    cursor: not-allowed;
  }
`;

const innerButton = css`
  transition: 0.2s;
  ${Container}:disabled &,
  ${Container}[disabled] & {
    margin-left: 16px;
    margin-right: -16px;
  }
`;

const LoadingContainer = styled.div`
  display: inline-block;
  position: absolute;
  width: 16px;
  height: 16px;
  top: 0;
  left: 0;

  div {
    box-sizing: border-box;
    display: block;
    position: absolute;
    width: 20px;
    height: 20px;
    margin: 6px;
    border: 3px solid #fff;
    border-radius: 50%;
    animation: loader 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
    border-color: #fff transparent transparent transparent;

    &:nth-child(1) {
      animation-delay: -0.45s;
    }

    &:nth-child(2) {
      animation-delay: -0.3s;
    }

    &:nth-child(3) {
      animation-delay: -0.15s;
    }

    @keyframes loader {
      0% {
        transform: rotate(0deg);
      }
      100% {
        transform: rotate(360deg);
      }
    }
  }
`;

function Loading() {
  return (
    <LoadingContainer>
      <div />
      <div />
      <div />
      <div />
    </LoadingContainer>
  );
}

export default function Button(props) {
  return (
    <Container {...props} disabled={props.isLoading || props.disabled}>
      {props.isLoading && <Loading />}
      <div css={innerButton}>{props.children}</div>
    </Container>
  );
}

export function AsyncButton(props) {
  const [isLoading, setLoading] = useState(false);

  const onClick = () => {
    const afterClick = props.onClick && props.onClick();
    if (!afterClick || typeof afterClick.then !== "function") {
      return;
    }
    setLoading(true);
    const done = () => setLoading(false);
    afterClick.then(done).catch(done);
  };

  return <Button {...props} isLoading={isLoading} onClick={onClick} />;
}

Button.defaultProps = {
  isLoading: false,
};
